// ENG-7891 / ADR-0049 slice 5 (stragglers) - the layout-aware DURABLE-STATE KEY.
//
// Slices 2a/2b/3 made `~/.augmented/{agent_id}` the real per-agent host dir and
// `~/.augmented/{codeName}` a relative compatibility symlink to it. Everything
// reached by a path *under* that dir follows the symlink (inode-match), so a
// rename is transparent - the point of the layout.
//
// A few pieces of DURABLE per-agent state are keyed by a codename string that is
// NOT such a path, so they follow no symlink and a rename would orphan them.
// ADR-0049 §"Durable vs ephemeral rule" converges exactly these on the
// rename-stable agent_id:
//   - the host-only egress allowlist file `~/.augmented/_egress/<key>.txt`
//   - the kanban-nudge backoff sidecar (`kanban-nudge-state.json`)
//   - (follow-up) the circuit-breaker state in `manager-state.json`
// The rule is deliberately narrow: EPHEMERAL runtime names - tmux `agt-<cn>`,
// docker `agt-<cn>` / `agt-squid-<cn>` / `agt-net-<cn>` - STAY codename-keyed and
// are simply recreated at the next spawn; a rename's residue there is "respawn",
// not "migrate". So this key is for durable artefacts only, never process /
// container / network names.
//
// It reads the agent_id straight from the symlink TARGET (the link is created as
// `symlinkSync(agentId, codeNamePath)`, so `basename(realpath())` is the
// agent_id), needing only the codeName every call site already has - no
// `agentId` threading. Legacy agents (no symlink) and not-yet-provisioned agents
// resolve to the codeName unchanged, so on the entire pre-arming fleet (no
// symlink exists until ADR-0049 is armed) this is a provable no-op.
//
// KEEP IN SYNC with `resolveRealAgentPath` (persistent-session.ts) and the core
// `getAgentDir` resolver: identical symlink contract, different return value -
// the identity KEY, not the resolved path.

import { lstatSync, realpathSync } from 'node:fs';
import { basename, dirname, join } from 'node:path';
import { homedir } from 'node:os';

/** agent_id is a v4 UUID (see agents.agent_id). Used to reject a corrupted
 * symlink target: if the link does not point at a UUID-named sibling the layout
 * contract is broken, so we fail SAFE to the codeName (legacy behaviour) rather
 * than key durable state on a garbage value. UUID shape alone is not enough -
 * host filesystems can hold UUID-named paths elsewhere - so the target's parent
 * must also be the agent's own `~/.augmented` dir (see agentRuntimeKey). */
const AGENT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Resolve the codename-or-agent_id key for an agent's DURABLE runtime state.
 * Returns the agent_id when the agent is on the id-keyed layout (its
 * `~/.augmented/{codeName}` entry is a symlink to a UUID-named dir), else the
 * codeName unchanged.
 */
export function agentRuntimeKey(codeName: string, homeDir?: string): string {
  const home = homeDir ?? (process.env.HOME?.trim() || homedir());
  const codeNamePath = join(home, '.augmented', codeName); // agent-dir-allow: durable-state-key resolver, mirrors resolveRealAgentPath (ADR-0049 slice 5)
  try {
    if (lstatSync(codeNamePath).isSymbolicLink()) {
      // The contract is a SIBLING link (`~/.augmented/{codeName}` -> `{agent_id}`).
      // Require the resolved target's parent to be this agent's own `~/.augmented`
      // dir, so a tampered link to an unrelated UUID-named path (e.g. `/tmp/<uuid>`)
      // can't redirect durable state to a foreign key. realpath both sides so a
      // symlinked HOME compares equal.
      const augmentedDir = realpathSync(join(home, '.augmented')); // agent-dir-allow: durable-state-key resolver sibling check (ADR-0049 slice 5)
      const resolvedTarget = realpathSync(codeNamePath);
      const target = basename(resolvedTarget);
      if (dirname(resolvedTarget) === augmentedDir && AGENT_ID_RE.test(target)) {
        return target;
      }
    }
  } catch {
    // Not provisioned yet (fresh agent) or a broken/dangling link - fall back
    // to the codeName, i.e. legacy behaviour.
  }
  return codeName;
}
